import { Breadcrumbs, Button, Menu } from "@mui/material";
import MachineSwitcherDropdown from "./MachineSwitcherDropdown";
import { ProcessGroupSwitcherDropdown } from "./ProcessGroupSwitcherDropdown";
import { WorkScheduleSwitcherDropdown } from "./WorkScheduleSwitcherDropdown";
import styled from "styled-components/macro";
import { BREAKPOINT_DESKTOP_XL, BREAKPOINT_TABLET } from "constants/constants";
import {
  BREADCRUMBS_GAP_REMS,
  useBreadcrumbDropdowns,
} from "./useBreadcrumbDropdowns";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import { darkBlue } from "constants/colors";
import { ConfigMenuItems, getConfigMenuIcon } from "./ConfigMenuItems";

export function TopBarBreadcrumbs({
  isBackButtonVisible,
}: {
  isBackButtonVisible: boolean;
}) {
  const { showWSDropdown, showPGSDropdown, showMSDropdown } =
    useBreadcrumbDropdowns();

  // Check if we're on a configuration page
  const { pathname } = useLocation();
  const isConfigPage = pathname.includes("/config/");

  return (
    <BreadcrumbsStyles $isBackButtonVisible={isBackButtonVisible}>
      <Breadcrumbs separator="">
        {/* Configuration dropdown for config pages */}
        {isConfigPage && (
          <BreadcrumbItem className="breadcrumb-item-config">
            <ConfigurationDropdown />
          </BreadcrumbItem>
        )}
        <BreadcrumbItem
          className={`breadcrumb-item-ws ${showWSDropdown ? "" : "hidden"}`}
        >
          <WorkScheduleSwitcherDropdown />
        </BreadcrumbItem>
        <BreadcrumbItem
          className={`breadcrumb-item-pg ${showPGSDropdown ? "" : "hidden"}`}
        >
          <BreadcrumbSeparator
            className={showPGSDropdown && showWSDropdown ? "" : "hidden"}
            style={{
              marginLeft: `${
                showPGSDropdown && !showWSDropdown ? -BREADCRUMBS_GAP_REMS : 0
              }rem`,
              width: showPGSDropdown && !showWSDropdown ? 0 : "auto",
            }}
          />
          <ProcessGroupSwitcherDropdown />
        </BreadcrumbItem>
        <BreadcrumbItem
          className={`breadcrumb-item-ms ${showMSDropdown ? "" : "hidden"}`}
        >
          <BreadcrumbSeparator
            className={
              showMSDropdown && (showWSDropdown || showPGSDropdown)
                ? ""
                : "hidden"
            }
          />
          <MachineSwitcherDropdown />
        </BreadcrumbItem>
      </Breadcrumbs>
    </BreadcrumbsStyles>
  );
}

const BreadcrumbsStyles = styled.div<{ $isBackButtonVisible: boolean }>`
  margin-left: ${props => (props.$isBackButtonVisible ? 100 : 50)}px;
  .MuiBreadcrumbs-ol {
    flex-wrap: nowrap;
    gap: ${BREADCRUMBS_GAP_REMS}rem;
  }
  .MuiBreadcrumbs-separator {
    display: none;
  }
  .breadcrumb-item {
    display: flex;
    align-items: center;
  }
  @media screen and (min-width: ${BREAKPOINT_DESKTOP_XL}px) {
    .MuiBreadcrumbs-ol {
      margin-left: 60px;
      gap: ${BREADCRUMBS_GAP_REMS * 2}rem;
    }
  }
`;

const BreadcrumbItem = styled.div`
  display: flex;
  align-items: center;
  @media (max-width: ${BREAKPOINT_TABLET}px) {
    &.hidden {
      width: 0;
    }
  }
`;

function BreadcrumbSeparator({ className = "", ...props }) {
  return (
    <BreadcrumbSeparatorStyles
      className={`breadcrumb-separator ${className}`}
      {...props}
    >
      {"/"}
    </BreadcrumbSeparatorStyles>
  );
}
const BreadcrumbSeparatorStyles = styled.div`
  transform: translateX(-8px);
  opacity: 1;
  transition: opacity 0.3s;
  @media screen and (max-width: ${BREAKPOINT_TABLET}px) {
    display: none;
  }
  &.hidden {
    opacity: 0;
    pointer-events: none;
  }
  @media screen and (min-width: ${BREAKPOINT_DESKTOP_XL}px) {
    font-size: 2em;
    transform: translateX(-16px);
  }
`;

// Configuration dropdown for top bar
export function ConfigurationDropdown({
  onSelect,
}: {
  onSelect?: (path: string) => void;
}) {
  const { translate } = useFlightTranslation();
  const ckey = useCustomerKey();
  const { pathname } = useLocation();

  // Determine the current configuration section
  const getCurrentConfigName = () => {
    if (pathname.includes("/config/skuTable")) {
      return translate("SKUs");
    } else if (pathname.includes("/config/users")) {
      return translate("Users");
    } else if (pathname.includes("/config/shiftSchedules")) {
      return translate("Shift Schedules");
    } else if (pathname.includes("/config/alertRules")) {
      return translate("Alert Rules");
    } else if (pathname.includes("/config/adlRules")) {
      return translate("ADL Rules");
    } else if (pathname.includes("/config/sense")) {
      return translate("Sense Configs");
    }
    return translate("Configuration");
  };

  // For Configuration menu
  const [configMenuAnchorEl, setConfigMenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const configMenuOpen = Boolean(configMenuAnchorEl);

  const handleConfigMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setConfigMenuAnchorEl(event.currentTarget);
    onSelect?.(pathname);
  };

  const handleConfigMenuClose = () => {
    setConfigMenuAnchorEl(null);
  };

  return (
    <>
      <Button
        onClick={handleConfigMenuClick}
        startIcon={getConfigMenuIcon(
          pathname.split("/")[3] as
            | "skuTable"
            | "users"
            | "shiftSchedules"
            | "alertRules"
            | "adlRules"
        )}
        endIcon={
          <KeyboardArrowDown
            style={{
              transform: configMenuOpen ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.3s",
            }}
          />
        }
        variant="text"
        color="inherit"
        size="small"
        style={{ color: "white" }}
      >
        {getCurrentConfigName()}
      </Button>
      <StyledConfigMenu
        anchorEl={configMenuAnchorEl}
        open={configMenuOpen}
        onClose={handleConfigMenuClose}
        MenuListProps={{
          "aria-labelledby": "config-menu-button",
        }}
        PaperProps={{
          style: {
            width: 220,
          },
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <ConfigMenuItems
          ckey={ckey}
          pathname={pathname}
          handleClose={handleConfigMenuClose}
        />
      </StyledConfigMenu>
    </>
  );
}

const StyledConfigMenu = styled(Menu)`
  a {
    color: #007eae !important;
  }
  .MuiSvgIcon-root {
    color: ${darkBlue};
  }
`;
